import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Plus, Trash2, ExternalLink, Eye, BarChart3, QrCode, Download, Copy, Pencil, Sparkles } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { Tables } from '@/integrations/supabase/types';
import ReviewFormBuilder from './ReviewFormBuilder';
import { FormBuilderV2Wrapper } from './FormBuilderV2Wrapper';
import ReviewProcessFunnelV2 from '@/components/analytics/ReviewProcessFunnelV2';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { formatDateForUI } from '@/lib/core/time-formatting';
import { UnifiedSkeleton } from '@/components/ui/unified-skeleton';

const BASE_URL = 'https://taginn.app';

interface ReviewForm {
  id: string;
  business_id: string;
  title: string;
  active_status: boolean;
  created_at: string;
  updated_at: string;
  builder_version?: number;
  questions?: Tables<'questions'>[];
}

interface ReviewFormManagerProps {
  businessId: string | null;
}

const ReviewFormManager: React.FC<ReviewFormManagerProps> = ({ businessId }) => {
  const { session } = useAuth();
  const { setActiveTab } = useDashboardTab();
  const [forms, setForms] = useState<ReviewForm[]>([]);
  const [loading, setLoading] = useState(true);

  // localStorage key for builder state
  const BUILDER_STATE_KEY = `reviewFormBuilderState_${businessId}`;

  // Load builder state from localStorage
  const loadBuilderState = () => {
    try {
      const saved = localStorage.getItem(BUILDER_STATE_KEY);
      return saved ? JSON.parse(saved) : { showBuilder: false, editingFormId: null };
    } catch (error) {
      console.error('Error loading builder state:', error);
      return { showBuilder: false, editingFormId: null };
    }
  };

  const builderState = loadBuilderState();

  const [showBuilder, setShowBuilder] = useState(false); // Always start with false, restore after forms load
  const [editingForm, setEditingForm] = useState<ReviewForm | null>(null);
  const [formStats, setFormStats] = useState<{[key: string]: {submissions: number, avgRating: number}}>({});
  const [starThreshold, setStarThreshold] = useState(4);
  const [hasRestoredState, setHasRestoredState] = useState(false); // Track if we've restored from localStorage
  const [useV2Builder, setUseV2Builder] = useState<boolean>(() => {
    // Load preference from localStorage, default to false (classic builder)
    try {
      const saved = localStorage.getItem('preferV2FormBuilder');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [showBuilderSelector, setShowBuilderSelector] = useState(false); // Show version selector before opening builder
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<string | null>(null);

  // Save builder state to localStorage - but ONLY after initial restoration
  useEffect(() => {
    if (!hasRestoredState) return; // Don't save until we've restored

    const state = {
      showBuilder,
      editingFormId: editingForm?.id || null,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(BUILDER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving builder state:', error);
    }
  }, [showBuilder, editingForm, BUILDER_STATE_KEY, hasRestoredState]);

  // Restore editing form and builder state AFTER forms have loaded
  useEffect(() => {
    if (hasRestoredState) return; // Only restore once

    if (forms.length > 0) {
      if (builderState.editingFormId) {
        const formToEdit = forms.find(f => f.id === builderState.editingFormId);
        if (formToEdit) {
          setEditingForm(formToEdit);
          // Only show builder after editing form is restored
          if (builderState.showBuilder) {
            setShowBuilder(true);
          }
        }
      } else if (builderState.showBuilder) {
        // User was creating a new form (no editingFormId)
        setShowBuilder(true);
      }

      // Mark that we've completed the restoration
      setHasRestoredState(true);
    }
  }, [forms, builderState.editingFormId, builderState.showBuilder, showBuilder, hasRestoredState]);

  useEffect(() => {
    if (businessId) {
      fetchReviewForms();
      fetchFormStats();
      fetchBusinessSettings();
    }
  }, [businessId]);

  const fetchReviewForms = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('review_forms')
        .select(`
          *,
          questions (*)
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setForms(data || []);
    } catch (error) {
      console.error('Error fetching review forms:', error);
      toast.error('Failed to load review forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormStats = async () => {
    if (!businessId) return;

    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('form_id, rating')
        .eq('business_id', businessId)
        .not('form_id', 'is', null);

      if (error) throw error;

      const stats: {[key: string]: {submissions: number, avgRating: number}} = {};
      
      (data || []).forEach(review => {
        if (review.form_id) {
          if (!stats[review.form_id]) {
            stats[review.form_id] = { submissions: 0, avgRating: 0 };
          }
          stats[review.form_id].submissions++;
          stats[review.form_id].avgRating += review.rating || 0;
        }
      });

      // Calculate averages
      Object.keys(stats).forEach(formId => {
        if (stats[formId].submissions > 0) {
          stats[formId].avgRating = stats[formId].avgRating / stats[formId].submissions;
        }
      });

      setFormStats(stats);
    } catch (error) {
      console.error('Error fetching form stats:', error);
    }
  };

  const fetchBusinessSettings = async () => {
    if (!businessId) return;

    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('review_redirect_star_threshold')
        .eq('id', businessId)
        .single();

      if (error) throw error;
      if (data?.review_redirect_star_threshold) {
        setStarThreshold(data.review_redirect_star_threshold);
      }
    } catch (error) {
      console.error('Error fetching business settings:', error);
    }
  };

  const handleCreateForm = () => {
    setEditingForm(null);
    setShowBuilderSelector(true);
  };

  const handleEditForm = (form: ReviewForm) => {
    setEditingForm(form);
    setShowBuilderSelector(true);
  };

  const handleSelectBuilderVersion = (useV2: boolean) => {
    setUseV2Builder(useV2);
    // Save preference
    try {
      localStorage.setItem('preferV2FormBuilder', String(useV2));
    } catch (error) {
      console.error('Error saving builder preference:', error);
    }
    setShowBuilderSelector(false);
    setShowBuilder(true);
  };

  const handleBuilderComplete = () => {
    setShowBuilder(false);
    setShowBuilderSelector(false);
    setEditingForm(null);
    // Clear builder state from localStorage
    try {
      localStorage.removeItem(BUILDER_STATE_KEY);
    } catch (error) {
      console.error('Error clearing builder state:', error);
    }
    fetchReviewForms(); // Refresh the list
    fetchFormStats(); // Refresh stats
  };

  const openDeleteDialog = (formId: string) => {
    setFormToDelete(formId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteForm = async (formId: string) => {
    try {
      // Delete in the correct order to respect foreign key constraints
      
      // 1. Delete review_funnel_events first
      const { error: funnelError } = await supabase
        .from('review_funnel_events')
        .delete()
        .eq('form_id', formId);
      
      if (funnelError) throw new Error(`Failed to delete funnel events: ${funnelError.message}`);

      // 2. Delete questions second
      const { error: questionsError } = await supabase
        .from('questions')
        .delete()
        .eq('form_id', formId);
      
      if (questionsError) throw new Error(`Failed to delete questions: ${questionsError.message}`);

      // 3. Delete reviews (if any exist)
      const { error: reviewsError } = await supabase
        .from('reviews')
        .delete()
        .eq('form_id', formId);
      
      if (reviewsError) throw new Error(`Failed to delete reviews: ${reviewsError.message}`);

      // 4. Finally delete the review form itself
      const { error: formError } = await supabase
        .from('review_forms')
        .delete()
        .eq('id', formId);

      if (formError) throw new Error(`Failed to delete form: ${formError.message}`);

      toast.success('Review form and all related data deleted successfully');
      fetchReviewForms();
      fetchFormStats();
    } catch (error) {
      console.error('Error deleting review form:', error);
      toast.error(`Failed to delete review form: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const toggleFormStatus = async (formId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('review_forms')
        .update({ active_status: !currentStatus })
        .eq('id', formId);

      if (error) throw error;
      toast.success(`Form ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchReviewForms();
      fetchFormStats();
    } catch (error) {
      console.error('Error updating form status:', error);
      toast.error('Failed to update form status');
    }
  };

  const downloadQRCode = async (formId: string, formTitle: string) => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(`${BASE_URL}/review/${formId}`)}`;

    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formTitle.replace(/\s+/g, '-').toLowerCase()}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QR Code downloaded successfully');
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error('Failed to download QR code');
    }
  };

  const copyFormUrl = (formId: string) => {
    const url = `${BASE_URL}/review/${formId}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Form URL copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy URL to clipboard');
    });
  };

  const duplicateForm = async (form: ReviewForm) => {
    try {
      const { data: newForm, error: formError } = await supabase
        .from('review_forms')
        .insert({
          business_id: form.business_id,
          title: `${form.title} (Copy)`,
          active_status: false // Start as inactive
        })
        .select()
        .single();

      if (formError) throw formError;

      // Copy questions if they exist
      if (form.questions && form.questions.length > 0) {
        const questionsToInsert = form.questions.map(q => ({
          form_id: newForm.id,
          type: q.type,
          text: q.text,
          required: q.required,
          order: q.order,
          options: q.options
        }));

        const { error: questionsError } = await supabase
          .from('questions')
          .insert(questionsToInsert);

        if (questionsError) throw questionsError;
      }

      toast.success('Form duplicated successfully');
      fetchReviewForms();
      fetchFormStats();
    } catch (error) {
      console.error('Error duplicating form:', error);
      toast.error('Failed to duplicate form');
    }
  };

  if (loading) {
    return <UnifiedSkeleton variant="tab" />;
  }

  // Show builder version selector
  if (showBuilderSelector) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setShowBuilderSelector(false);
              setEditingForm(null);
            }}
          >
            ← Back
          </Button>
        </div>

        <div className="max-w-3xl mx-auto">
          <h3 className="text-lg font-medium mb-4 text-center">Choose Form Builder</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Classic Builder */}
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${!useV2Builder ? 'ring-2 ring-primary' : ''}`}
              onClick={() => handleSelectBuilderVersion(false)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Classic Builder</CardTitle>
                  {!useV2Builder && (
                    <Badge variant="secondary">Last Used</Badge>
                  )}
                </div>
                <CardDescription>
                  The original form builder you're familiar with
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Simple drag-and-drop interface</li>
                  <li>• Basic question types</li>
                  <li>• Quick and easy setup</li>
                  <li>• Proven and stable</li>
                </ul>
                <Button className="w-full mt-4" variant="outline">
                  Use Classic Builder
                </Button>
              </CardContent>
            </Card>

            {/* New V2 Builder */}
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${useV2Builder ? 'ring-2 ring-primary' : ''}`}
              onClick={() => handleSelectBuilderVersion(true)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    New Builder
                    <Sparkles className="h-4 w-4 text-yellow-500" />
                  </CardTitle>
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    Beta
                  </Badge>
                </div>
                <CardDescription>
                  Modern Typeform-style experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• 13 question types</li>
                  <li>• Mobile/desktop device toggle</li>
                  <li>• Live preview pane</li>
                  <li>• Advanced branching logic</li>
                  <li>• Smooth animations</li>
                  <li>• Keyboard navigation</li>
                </ul>
                <Button className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                  Try New Builder
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show builder if in creation or editing mode
  if (showBuilder) {
    const handleCancel = () => {
      setShowBuilder(false);
      setShowBuilderSelector(false);
      setEditingForm(null);
      // Clear builder state from localStorage
      try {
        localStorage.removeItem(BUILDER_STATE_KEY);
      } catch (error) {
        console.error('Error clearing builder state:', error);
      }
    };

    // Use V2 builder
    if (useV2Builder) {
      return (
        <FormBuilderV2Wrapper
          businessId={businessId}
          existingFormId={editingForm?.id}
          onComplete={handleBuilderComplete}
          onCancel={handleCancel}
        />
      );
    }

    // Use classic builder
    return (
      <ReviewFormBuilder
        businessId={businessId}
        existingForm={editingForm}
        onComplete={handleBuilderComplete}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="w-full space-y-4">
        {forms.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Create Form Card - Empty State */}
            <Card
              className="overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:shadow-lg transition-all duration-200 h-fit cursor-pointer bg-muted/30"
              onClick={handleCreateForm}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-muted rounded-lg">
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-muted-foreground">Create Your First Form</CardTitle>
                      <CardDescription className="text-xs text-muted-foreground">
                        Start collecting feedback
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="space-y-4">
                  {/* QR Code and Action Buttons Placeholder */}
                  <div className="flex gap-4 items-start">
                    {/* Plus Icon where QR Code would be */}
                    <div className="w-40 h-40 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Plus className="h-16 w-16 text-muted-foreground/40" />
                    </div>

                    {/* Status, Action Buttons, and Info Placeholder */}
                    <div className="flex-1 flex flex-col gap-2">
                      {/* Status Badge Placeholder */}
                      <div className="flex items-center gap-2">
                        <div className="px-2 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                          New Form
                        </div>
                      </div>

                      {/* Action Buttons Placeholder */}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="w-full flex items-center gap-1 opacity-50"
                      >
                        <Download className="h-3 w-3" />
                        Download QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="w-full flex items-center gap-1 opacity-50"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Copy Link
                      </Button>

                      {/* Info Text Placeholder */}
                      <p className="text-xs text-muted-foreground mt-1">
                        Your QR code will appear here
                      </p>

                      <p className="text-xs text-muted-foreground">
                        Click to create
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
              {forms.map((form) => (
                <Card key={form.id} className="overflow-hidden border hover:shadow-lg transition-shadow duration-200 h-full flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary dark:text-primary">
                          <MessageSquare className="h-4 w-4" />
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold">{form.title}</CardTitle>
                          <CardDescription className="text-xs">
                            {form.questions?.length || 0} questions
                            {formStats[form.id] && (
                              <span className="ml-2 text-primary">
                                • {formStats[form.id].submissions} submissions
                                {formStats[form.id].avgRating > 0 && (
                                  <span className="ml-1">
                                    • ⭐ {formStats[form.id].avgRating.toFixed(1)}
                                  </span>
                                )}
                              </span>
                            )}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditForm(form)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border hover:border-blue-400"
                          title="Edit form"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => duplicateForm(form)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border hover:border-green-400"
                          title="Duplicate form"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeleteDialog(form.id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border hover:border-red-400"
                          title="Delete form"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 flex-1">
                    <div className="space-y-4">
                      {/* QR Code and Action Buttons */}
                      <div className="flex gap-4 items-start">
                        {/* QR Code */}
                        <div className="w-40 h-40 bg-white border rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(`${BASE_URL}/review/${form.id}`)}`}
                            alt="Form QR Code"
                            className="w-36 h-36"
                          />
                        </div>

                        {/* Status, Action Buttons, and Info */}
                        <div className="flex-1 flex flex-col gap-2">
                          {/* Status Badge with Switch */}
                          <div className="flex items-center gap-2">
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                              form.active_status
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {form.active_status ? 'Active' : 'Inactive'}
                            </div>
                            <Switch
                              id={`form-active-${form.id}`}
                              checked={form.active_status}
                              onCheckedChange={() => toggleFormStatus(form.id, form.active_status)}
                              className="scale-75"
                            />
                          </div>

                          {/* Action Buttons */}
                          <Button
                            size="sm"
                            onClick={() => downloadQRCode(form.id, form.title)}
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground border-primary flex items-center gap-1"
                          >
                            <Download className="h-3 w-3" />
                            Download QR
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyFormUrl(form.id)}
                            className="w-full flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Copy Link
                          </Button>

                          {/* Info Text */}
                          <p className="text-xs text-muted-foreground mt-1">
                            Customers scan to access form
                          </p>

                          <p className="text-xs text-muted-foreground">
                            Created {formatDateForUI(form.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Status Alert - always rendered to maintain consistent height */}
                      <Alert className={`border-yellow-200 bg-yellow-50 dark:bg-yellow-900/10 dark:border-yellow-800 ${form.active_status ? 'invisible' : ''}`}>
                        <AlertDescription className="text-yellow-700 dark:text-yellow-300 text-xs">
                          <span className="font-medium">Inactive:</span> Activate to collect reviews
                        </AlertDescription>
                      </Alert>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Create Form Card */}
              <Card
                className="overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:shadow-lg transition-all duration-200 h-full flex flex-col cursor-pointer bg-muted/30"
                onClick={handleCreateForm}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-muted rounded-lg">
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-muted-foreground">Add Review Form</CardTitle>
                        <CardDescription className="text-xs text-muted-foreground">
                          Create new form
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 flex-1">
                  <div className="space-y-4">
                    {/* QR Code and Action Buttons Placeholder */}
                    <div className="flex gap-4 items-start">
                      {/* Plus Icon where QR Code would be */}
                      <div className="w-40 h-40 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Plus className="h-16 w-16 text-muted-foreground/40" />
                      </div>

                      {/* Status, Action Buttons, and Info Placeholder */}
                      <div className="flex-1 flex flex-col gap-2">
                        {/* Status Badge Placeholder */}
                        <div className="flex items-center gap-2">
                          <div className="px-2 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                            New Form
                          </div>
                        </div>

                        {/* Action Buttons Placeholder */}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          className="w-full flex items-center gap-1 opacity-50"
                        >
                          <Download className="h-3 w-3" />
                          Download QR
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          className="w-full flex items-center gap-1 opacity-50"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Copy Link
                        </Button>

                        {/* Info Text Placeholder */}
                        <p className="text-xs text-muted-foreground mt-1">
                          QR code will appear here
                        </p>

                        <p className="text-xs text-muted-foreground">
                          Click to create
                        </p>
                      </div>
                    </div>

                    {/* Invisible Alert placeholder to match other cards height */}
                    <Alert className="invisible border-yellow-200">
                      <AlertDescription className="text-xs">
                        <span className="font-medium">Inactive:</span> Activate to collect reviews
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {/* Review Process Funnel Analytics */}
        {businessId && (
          <div className="mt-4">
            <ReviewProcessFunnelV2
              businessId={businessId}
              starThreshold={starThreshold}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review form? This will also delete all related analytics data and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFormToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (formToDelete) {
                  handleDeleteForm(formToDelete);
                  setFormToDelete(null);
                }
                setDeleteDialogOpen(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewFormManager;